import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StarRating from "@/components/StarRating";
import { toast } from "sonner";
import { Check, X, ArrowRight } from "lucide-react";

const STATUS_COLORS = {
  pending: "text-gold border-gold",
  confirmed: "text-forest border-forest",
  completed: "text-forest-muted border-cream-border",
  cancelled: "text-destructive border-destructive",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, []);

  const updateStatus = async (booking_id, status) => {
    try {
      await api.patch(`/bookings/${booking_id}`, { status });
      toast.success(`Lesson ${status}`);
      fetchBookings();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const now = new Date();
  const upcoming = bookings.filter((b) => b.status !== "cancelled" && b.status !== "completed" && new Date(`${b.date}T${b.start_time}`) >= now);
  const past = bookings.filter((b) => !upcoming.includes(b));

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-16 md:pt-20 pb-10">
        <div className="eyebrow">{user?.role === "coach" ? "Coach Studio" : "Your Lessons"}</div>
        <h1 className="mt-4 font-serif text-5xl md:text-7xl text-forest leading-[0.95]">
          Welcome back, <em className="italic text-gold font-light">{user?.name?.split(" ")[0]}</em>.
        </h1>
        {user?.role === "student" && (
          <p className="mt-6 font-light text-lg text-forest-muted max-w-2xl leading-relaxed">
            Track your reservations, reschedule, and leave a review after each lesson. Ready for more? Browse the roster.
          </p>
        )}
      </section>

      {user?.role === "student" && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pb-8 flex gap-4">
          <Link to="/coaches" data-testid="dashboard-browse-btn" className="btn-primary">
            Find another coach <ArrowRight className="h-4 w-4 ml-3" />
          </Link>
        </section>
      )}

      <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pb-24">
        <div className="eyebrow">Upcoming</div>
        <div className="rule mt-3" />
        {loading ? (
          <div className="mt-8 font-serif text-xl text-forest-muted">Loading…</div>
        ) : upcoming.length === 0 ? (
          <div data-testid="dashboard-empty" className="mt-8 border border-cream-border bg-cream-light p-10">
            <h3 className="font-serif text-2xl text-forest">No upcoming lessons.</h3>
            {user?.role === "student" && (
              <p className="mt-2 text-forest-muted">
                <Link to="/coaches" className="underline decoration-gold underline-offset-4 hover:text-forest">Browse coaches</Link> to book one.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {upcoming.map((b) => (
              <BookingRow key={b.booking_id} b={b} role={user.role} onStatus={updateStatus} />
            ))}
          </div>
        )}

        <div className="eyebrow mt-20">Past & Cancelled</div>
        <div className="rule mt-3" />
        {past.length === 0 ? (
          <div className="mt-8 text-forest-muted font-light">Nothing here yet.</div>
        ) : (
          <div className="mt-8 space-y-6">
            {past.map((b) => (
              <BookingRow key={b.booking_id} b={b} role={user.role} onStatus={updateStatus} onReview={fetchBookings} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

function BookingRow({ b, role, onStatus, onReview }) {
  const [reviewing, setReviewing] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const submitReview = async () => {
    try {
      await api.post("/reviews", { coach_id: b.coach_id, booking_id: b.booking_id, rating, comment });
      toast.success("Review posted");
      setReviewing(false);
      onReview?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      data-testid={`booking-row-${b.booking_id}`}
      className="border border-cream-border bg-cream-light p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
    >
      <div className="md:col-span-3">
        <div className="eyebrow">Date</div>
        <div className="mt-2 font-serif text-2xl text-forest">{b.date}</div>
        <div className="text-sm text-forest-muted">{b.start_time}</div>
      </div>
      <div className="md:col-span-4">
        <div className="eyebrow">{role === "coach" ? "Student" : "Coach"}</div>
        <div className="mt-2 font-serif text-2xl text-forest">
          {role === "coach" ? b.student_name : b.coach_name}
        </div>
        <div className="text-sm text-forest-muted capitalize">{b.audience || "adult"} · {b.package_label || "Lesson"} · ${b.price}</div>
      </div>
      <div className="md:col-span-2">
        <div className="eyebrow">Status</div>
        <div className={`mt-2 inline-block border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS_COLORS[b.status]}`}>
          {b.status}
        </div>
      </div>
      <div className="md:col-span-3 flex flex-wrap gap-2 justify-end">
        {role === "coach" && b.status === "pending" && (
          <>
            <button onClick={() => onStatus(b.booking_id, "confirmed")} data-testid={`confirm-${b.booking_id}`} className="btn-primary !px-4 !py-2 text-[10px]">
              <Check className="h-3 w-3 mr-2" /> Confirm
            </button>
            <button onClick={() => onStatus(b.booking_id, "cancelled")} data-testid={`decline-${b.booking_id}`} className="btn-secondary !px-4 !py-2 text-[10px]">
              <X className="h-3 w-3 mr-2" /> Decline
            </button>
          </>
        )}
        {role === "coach" && b.status === "confirmed" && (
          <button onClick={() => onStatus(b.booking_id, "completed")} className="btn-primary !px-4 !py-2 text-[10px]">Mark complete</button>
        )}
        {role === "student" && (b.status === "pending" || b.status === "confirmed") && (
          <button onClick={() => onStatus(b.booking_id, "cancelled")} data-testid={`cancel-${b.booking_id}`} className="btn-secondary !px-4 !py-2 text-[10px]">Cancel</button>
        )}
        {role === "student" && b.status === "completed" && !reviewing && (
          <button onClick={() => setReviewing(true)} data-testid={`review-${b.booking_id}`} className="btn-primary !px-4 !py-2 text-[10px]">Leave a review</button>
        )}
      </div>

      {reviewing && (
        <div className="md:col-span-12 border-t border-cream-border pt-6 mt-2">
          <div className="eyebrow">Your review</div>
          <div className="mt-3 flex items-center gap-3">
            {[1, 2, 3, 4, 5].map((r) => (
              <button key={r} onClick={() => setRating(r)}>
                <StarRating value={rating >= r ? 5 : 0} className={r <= rating ? "" : "opacity-30"} />
              </button>
            ))}
            <span className="font-serif text-2xl text-forest ml-3">{rating}/5</span>
          </div>
          <textarea
            data-testid={`review-text-${b.booking_id}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="How was it?"
            rows={3}
            className="mt-4 field resize-none"
          />
          <div className="mt-4 flex gap-3">
            <button data-testid={`submit-review-${b.booking_id}`} onClick={submitReview} className="btn-primary">Post review</button>
            <button onClick={() => setReviewing(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
